import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { getCached, setCached, CACHE_KEYS } from "../utils/sessionCache";

/** Cuál de los dos banners promocionales de home corresponde mostrar (o ninguno). */
export type HomeBannerKind = "upsell" | "brand" | null;

/** Estado de la suscripción que amerita un banner informativo en la home. */
export type SubscriptionStatus = "cancelled" | "paused" | null;

export type HomeBannersState = {
  /**
   *  - "upsell": usuario de prueba gratis que ya generó ≥5 imágenes.
   *  - "brand": usuario pago sin análisis de marca (`config.media_agent_analysis`).
   *  - null: no corresponde ninguno (o aún cargando).
   */
  kind: HomeBannerKind;
  /** Imágenes gratis restantes (solo relevante para "upsell"). */
  freeImagesRemaining: number | null;
  /** País del usuario === Argentina (cambia el copy/CTA del upsell). */
  isArgentina: boolean;
  /**
   * Estado de la suscripción (INDEPENDIENTE de `kind`):
   *  - "cancelled": hay una cancelación programada a futuro.
   *  - "paused": la suscripción está pausada (reanuda a futuro).
   *  - null: ninguno.
   */
  status: SubscriptionStatus;
  /** Fecha de cancelación formateada (solo si status === "cancelled"). */
  cancelDate: string | null;
  /** Fecha de reanudación formateada (solo si status === "paused"). */
  resumeDate: string | null;
};

const EMPTY: HomeBannersState = {
  kind: null,
  freeImagesRemaining: null,
  isArgentina: false,
  status: null,
  cancelDate: null,
  resumeDate: null,
};

// Mismo formato que el Paywall ("20 de julio de 2026").
const formatDate = (raw: unknown): string | null => {
  if (!raw) return null;
  const d = new Date(raw as string);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const isFuture = (raw: unknown): boolean => {
  if (!raw) return false;
  const d = new Date(raw as string);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
};

/**
 * Resuelve qué banner de home mostrar, con las MISMAS condiciones que la
 * pantalla clásica de selección de productos. Fuente única para que el clásico
 * y la Interfaz V2 muestren exactamente los mismos banners.
 *
 * Banners promocionales (`kind`, mutuamente excluyentes):
 *  - Plan gratis + ≥5 imágenes generadas → "upsell" (prueba gratis).
 *  - Plan pago + sin `media_agent_analysis` → "brand" (describir la marca),
 *    salvo que `suppressBrand` esté activo.
 *
 * Estado de suscripción (`status`, ortogonal a `kind`):
 *  - Cancelación programada a futuro → "cancelled".
 *  - Reanudación programada a futuro (pausa) → "paused".
 * El consumidor decide si renderizar el banner de estado (ver `HomeBannersView`
 * con `showStatus`); hoy solo se muestra en mobile.
 *
 * @param opts.suppressBrand fuerza ocultar el banner de marca (ej. al volver de
 *   `/redes-sociales/descripcion-brand` con `hideBrandBanner`).
 */
export function useHomeBanners(opts?: {
  suppressBrand?: boolean;
}): HomeBannersState {
  const suppressBrand = opts?.suppressBrand ?? false;
  const [state, setState] = useState<HomeBannersState>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const idCompany = profile?.id_company as string | undefined;
        if (!idCompany) return;

        const { data: company } = await supabase
          .from("company_profiles")
          .select(
            "plan, pais, imagenes_restantes, fecha_cancelacion_programada, fecha_reactivacion_programada",
          )
          .eq("id_company", idCompany)
          .single();
        if (cancelled) return;

        const plan = (company as any)?.plan || "gratis";
        const pais = ((company as any)?.pais || "") as string;
        const imagenesRestantes =
          typeof (company as any)?.imagenes_restantes === "number"
            ? ((company as any).imagenes_restantes as number)
            : null;
        const isArgentina = pais.toLowerCase() === "argentina";
        const isPaidPlan = plan && plan !== "gratis";

        // Estado de la suscripción (ortogonal al banner promocional). Solo
        // aplica a usuarios pagos; un free no tiene cancelación/pausa.
        const cancelRaw = (company as any)?.fecha_cancelacion_programada;
        const resumeRaw = (company as any)?.fecha_reactivacion_programada;
        let status: SubscriptionStatus = null;
        let cancelDate: string | null = null;
        let resumeDate: string | null = null;
        if (isPaidPlan && isFuture(cancelRaw)) {
          status = "cancelled";
          cancelDate = formatDate(cancelRaw);
        } else if (isPaidPlan && isFuture(resumeRaw)) {
          status = "paused";
          resumeDate = formatDate(resumeRaw);
        }

        if (!isPaidPlan) {
          // Prueba gratis: upsell tras 5 imágenes generadas (sin contar errores).
          const { count } = await supabase
            .from("imagenes_generadas")
            .select("id", { count: "exact", head: true })
            .neq("has_error", true)
            .eq("id_company", idCompany);
          if (cancelled) return;
          setState({
            kind: (count || 0) >= 5 ? "upsell" : null,
            freeImagesRemaining: imagenesRestantes,
            isArgentina,
            status,
            cancelDate,
            resumeDate,
          });
          return;
        }

        // Plan pago: banner de marca si todavía no hay análisis de marca.
        if (suppressBrand) {
          setState({
            kind: null,
            freeImagesRemaining: null,
            isArgentina,
            status,
            cancelDate,
            resumeDate,
          });
          return;
        }
        let hasAnalysis = getCached<boolean>(CACHE_KEYS.COMPANY_CONFIG);
        if (hasAnalysis === null) {
          try {
            const { data: config } = await supabase
              .from("config")
              .select("media_agent_analysis")
              .eq("id_company", idCompany)
              .maybeSingle();
            hasAnalysis = !!config?.media_agent_analysis;
            setCached(CACHE_KEYS.COMPANY_CONFIG, hasAnalysis);
          } catch {
            hasAnalysis = false;
          }
        }
        if (cancelled) return;
        setState({
          kind: hasAnalysis ? null : "brand",
          freeImagesRemaining: null,
          isArgentina,
          status,
          cancelDate,
          resumeDate,
        });
      } catch {
        if (!cancelled) setState(EMPTY);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [suppressBrand]);

  return state;
}
